import {
  ensureSystemWorkspaceDoc,
  getSystemWorkspaceStore,
} from '@blocksuite/affine-block-streamlain';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { normalizeDatabaseShardManifest } from '@blocksuite/affine-model';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type { BlockModel } from '@blocksuite/store';
import { Map as YMap } from 'yjs';

export { ensureSystemWorkspaceDoc, getSystemWorkspaceStore };

export function resolveDatabaseStorageModel(
  hostModel: DatabaseBlockModel
): DatabaseBlockModel {
  const rootDocId = hostModel.props.databaseRootDocId;
  if (!rootDocId) {
    if (hasLegacyInlineDatabaseStorageModel(hostModel)) {
      return hostModel;
    }
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database block "${hostModel.id}" is missing databaseRootDocId; unsharded database storage is unsupported.`
    );
  }
  if (rootDocId === hostModel.store.id) {
    assertDatabaseStorageManifest(hostModel, rootDocId);
    return hostModel;
  }

  const databaseId = hostModel.props.databaseId ?? hostModel.id;
  const storageBlock = getDatabaseStorageBlock(
    hostModel,
    rootDocId,
    databaseId
  );
  if (!storageBlock || storageBlock.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database root document "${rootDocId}" does not contain database "${databaseId}".`
    );
  }

  assertDatabaseStorageManifest(storageBlock as DatabaseBlockModel, rootDocId);
  return storageBlock as DatabaseBlockModel;
}

export function assertDatabaseStorageManifest(
  storageModel: DatabaseBlockModel,
  rootDocId: string
): void {
  if (
    normalizeDatabaseShardManifest(storageModel.props.databaseShardManifest)
  ) {
    return;
  }
  const databaseId = storageModel.props.databaseId ?? storageModel.id;
  throw new BlockSuiteError(
    ErrorCode.DatabaseBlockError,
    `Database root document "${rootDocId}" has no row-shard manifest for database "${databaseId}".`
  );
}

export function hasValidDatabaseStorageManifest(
  storageModel: DatabaseBlockModel
): boolean {
  try {
    return (
      normalizeDatabaseShardManifest(
        storageModel.props.databaseShardManifest
      ) !== undefined
    );
  } catch {
    return false;
  }
}

export function hasLegacyInlineDatabaseStorageModel(
  hostModel: DatabaseBlockModel
): boolean {
  const yBlock = hostModel.yBlock;
  return (
    yBlock instanceof YMap &&
    hostModel.props.databaseId === hostModel.id &&
    hostModel.props.databaseStorageVersion === undefined &&
    yBlock.has('prop:cells') &&
    yBlock.has('prop:columns') &&
    yBlock.has('prop:views')
  );
}

export function getDatabaseStorageBlock(
  hostModel: DatabaseBlockModel,
  rootDocId: string,
  databaseId: string
): BlockModel | undefined {
  const rootDoc = getSystemWorkspaceStore(hostModel.store, rootDocId);
  return rootDoc?.getBlock(databaseId)?.model;
}

export function isDatabaseStorageModelReady(
  hostModel: DatabaseBlockModel
): boolean {
  const rootDocId = hostModel.props.databaseRootDocId;
  if (!rootDocId) {
    return hasLegacyInlineDatabaseStorageModel(hostModel);
  }
  if (rootDocId === hostModel.store.id) {
    return hasValidDatabaseStorageManifest(hostModel);
  }

  const databaseId = hostModel.props.databaseId ?? hostModel.id;
  const storageBlock = getDatabaseStorageBlock(
    hostModel,
    rootDocId,
    databaseId
  );
  return (
    storageBlock?.flavour === 'affine:database' &&
    hasValidDatabaseStorageManifest(storageBlock as DatabaseBlockModel)
  );
}

export function observeDatabaseStorageModelReady(
  hostModel: DatabaseBlockModel,
  onReady: () => void
): () => void {
  const rootDocId = hostModel.props.databaseRootDocId;
  if (!rootDocId || rootDocId === hostModel.store.id) {
    return () => {};
  }

  const rootDocRecord = ensureSystemWorkspaceDoc(hostModel.store, rootDocId);
  const checkReady = () => {
    if (isDatabaseStorageModelReady(hostModel)) {
      onReady();
    }
  };
  rootDocRecord.spaceDoc.on('update', checkReady);
  checkReady();
  return () => rootDocRecord.spaceDoc.off('update', checkReady);
}
